import { User, Bot } from "lucide-react";
import { motion } from "framer-motion";
import { useMemo } from "react";

interface ChatMessageProps {
  role: "user" | "assistant";
  content: string;
}

// Simple markdown parser for bold and lists
const parseMarkdown = (text: string): React.ReactNode[] => {
  const lines = text.split('\n');
  const result: React.ReactNode[] = [];
  let listItems: string[] = [];
  let inList = false;

  const parseInlineFormatting = (line: string): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];
    // Match bold (**text**) and italic (*text*) - bold first to avoid conflicts
    const regex = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
    let lastIndex = 0;
    let match;
    let keyIndex = 0;

    while ((match = regex.exec(line)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(line.slice(lastIndex, match.index));
      }
      
      if (match[2]) {
        // Bold text (**text**)
        parts.push(<strong key={`bold-${keyIndex++}`} className="font-semibold">{match[2]}</strong>);
      } else if (match[3]) {
        // Italic text (*text*)
        parts.push(<em key={`italic-${keyIndex++}`} className="italic">{match[3]}</em>);
      }
      
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < line.length) {
      parts.push(line.slice(lastIndex));
    }

    return parts.length > 0 ? parts : [line];
  };

  const flushList = () => {
    if (listItems.length > 0) {
      result.push(
        <ul key={`list-${result.length}`} className="list-disc list-inside space-y-1 my-2">
          {listItems.map((item, idx) => (
            <li key={idx} className="text-sm leading-relaxed">
              {parseInlineFormatting(item)}
            </li>
          ))}
        </ul>
      );
      listItems = [];
    }
    inList = false;
  };

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    
    // Check for bullet points (-, *, •)
    const bulletMatch = trimmedLine.match(/^[-*•]\s+(.+)$/);
    // Check for numbered lists (1., 2., etc)
    const numberedMatch = trimmedLine.match(/^\d+\.\s+(.+)$/);
    
    if (bulletMatch || numberedMatch) {
      inList = true;
      listItems.push(bulletMatch ? bulletMatch[1] : numberedMatch![1]);
    } else {
      flushList();
      
      if (trimmedLine === '') {
        result.push(<br key={`br-${index}`} />);
      } else {
        result.push(
          <span key={`line-${index}`}>
            {parseInlineFormatting(line)}
            {index < lines.length - 1 && <br />}
          </span>
        );
      }
    }
  });

  flushList();

  return result;
};

export const ChatMessage = ({ role, content }: ChatMessageProps) => {
  const isUser = role === "user";

  const formattedContent = useMemo(() => {
    if (isUser) {
      return <span className="whitespace-pre-wrap">{content}</span>;
    }
    return parseMarkdown(content);
  }, [content, isUser]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`flex gap-3 ${isUser ? "flex-row-reverse" : "flex-row"} mb-6`}
    >
      <div
        className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
          isUser ? "bg-gradient-to-br from-primary to-accent" : "bg-gradient-to-br from-secondary to-muted"
        }`}
      >
        {isUser ? <User className="w-5 h-5 text-white" /> : <Bot className="w-5 h-5 text-foreground" />}
      </div>

      <div className={`flex-1 max-w-[80%] ${isUser ? "items-end" : "items-start"} flex flex-col gap-2`}>
        <div
          className={`rounded-2xl px-4 py-3 ${
            isUser
              ? "bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20"
              : "bg-card border border-border"
          }`}
        >
          <div className="text-sm leading-relaxed text-foreground">{formattedContent}</div>
        </div>
      </div>
    </motion.div>
  );
};
